/* eslint-disable eag/match-named-export -- Pre-existing grab-bag file with several exports */
import {z} from '@hono/zod-openapi';

import {equipmentMappingRuleRequestSchema} from './EquipmentMappingRuleSchemas';

const advertisingMcpPlatformCodeSchema = z.string().trim().min(1).max(100);
export const advertisingMcpPageSchema = z.object({
  platformCode: advertisingMcpPlatformCodeSchema,
  limit: z.number().int().min(1).max(100).default(20),
  offset: z.number().int().min(0).default(0),
});
export const advertisingMcpMakeSchema = z.object({
  platformCode: advertisingMcpPlatformCodeSchema,
  make: z.string().trim().min(1).max(100),
});
export const advertisingMcpListVehicleMappingIssuesSchema = z.object({
  platformCode: advertisingMcpPlatformCodeSchema,
  runId: z.string().uuid().optional(),
  status: z
    .enum([
      'mapped',
      'taxonomy_missing',
      'ambiguous',
      'rule_invalid',
      'explicitly_unsupported',
      'evaluation_error',
    ])
    .optional(),
  make: z.string().trim().min(1).max(100).optional(),
  limit: z.number().int().min(1).max(100).default(100),
  cursor: z.string().min(1).max(100).optional(),
});
export const advertisingMcpListSourceVehicleMakesSchema = z.object({
  platformCode: advertisingMcpPlatformCodeSchema,
  runId: z.string().uuid().optional(),
  limit: z.number().int().min(1).max(250).default(250),
  cursor: z.string().min(1).max(100).optional(),
});
export const advertisingMcpPreviewUnsupportedVehicleMappingSchema = z.object({
  platformCode: advertisingMcpPlatformCodeSchema,
  runId: z.string().uuid(),
  issueId: z.string().uuid(),
  scope: z.enum(['make', 'model', 'fuel', 'hybrid']),
  reason: z.string().trim().max(1000).default(''),
});
export const advertisingMcpRerunVehicleMappingCoverageSchema = z.object({
  platformCode: advertisingMcpPlatformCodeSchema,
});
export const advertisingMcpListEquipmentMappingIssuesSchema = z.object({
  platformCode: advertisingMcpPlatformCodeSchema,
  runId: z.string().uuid().optional(),
  status: z
    .enum([
      'mapped',
      'taxonomy_missing',
      'ambiguous',
      'rule_invalid',
      'explicitly_unsupported',
      'evaluation_error',
    ])
    .optional(),
  search: z.string().trim().min(1).max(100).optional(),
  limit: z.number().int().min(1).max(100).default(100),
  cursor: z.string().min(1).max(100).optional(),
});
export const advertisingMcpSuggestVehicleTargetsSchema = z.object({
  platformCode: advertisingMcpPlatformCodeSchema,
  runId: z.string().uuid().optional(),
  source: z.object({
    make: z.string().trim().min(1).max(100),
    modelFamily: z.string().trim().min(1).max(100),
  }),
  limit: z.number().int().min(1).max(20).default(5),
});
export const advertisingMcpSuggestEquipmentTargetsSchema = z.object({
  platformCode: advertisingMcpPlatformCodeSchema,
  runId: z.string().uuid().optional(),
  sourceKey: z.string().trim().min(1).max(200),
  sourceLabel: z.string().trim().min(1).max(200).optional(),
  limit: z.number().int().min(1).max(20).default(5),
});
export const advertisingMcpPreviewManualVehicleMappingSchema = z.object({
  platformCode: advertisingMcpPlatformCodeSchema,
  source: z.object({
    make: z.string().trim().min(1).max(100),
    modelFamily: z.string().trim().min(1).max(100),
  }),
  target: z.object({
    make: z.string().trim().min(1).max(100),
    modelFamily: z.string().trim().min(1).max(100),
  }),
  reason: z.string().trim().max(1000).optional(),
});
const advertisingMcpVehicleMappingBatchItemSchema = z.discriminatedUnion('kind', [
  z.object({
    itemKey: z.string().trim().min(1).max(100),
    kind: z.literal('mapping_rule'),
    source: advertisingMcpPreviewManualVehicleMappingSchema.shape.source,
    target: advertisingMcpPreviewManualVehicleMappingSchema.shape.target,
    reason: z.string().trim().max(1000).optional(),
  }),
  z.object({
    itemKey: z.string().trim().min(1).max(100),
    kind: z.literal('unsupported'),
    runId: z.string().uuid(),
    issueId: z.string().uuid(),
    scope: z.enum(['make', 'model', 'fuel', 'hybrid']),
    reason: z.string().trim().max(1000).default(''),
  }),
]);
export const advertisingMcpPreviewVehicleMappingBatchSchema = z.object({
  platformCode: advertisingMcpPlatformCodeSchema,
  items: z.array(advertisingMcpVehicleMappingBatchItemSchema).min(1).max(100),
});
export const advertisingMcpCreateVehicleMappingProposalsSchema = z.object({
  platformCode: advertisingMcpPlatformCodeSchema,
  batchIdempotencyKey: z.string().uuid(),
  items: z.array(advertisingMcpVehicleMappingBatchItemSchema).min(1).max(100),
});
const advertisingMcpEquipmentMappingBatchItemSchema = z.object({
  itemKey: z.string().trim().min(1).max(100),
  request: equipmentMappingRuleRequestSchema,
});
export const advertisingMcpPreviewEquipmentMappingBatchSchema = z.object({
  platformCode: advertisingMcpPlatformCodeSchema,
  items: z.array(advertisingMcpEquipmentMappingBatchItemSchema).min(1).max(100),
});
export const advertisingMcpCreateEquipmentMappingProposalsSchema = z.object({
  platformCode: advertisingMcpPlatformCodeSchema,
  batchIdempotencyKey: z.string().uuid(),
  items: z.array(advertisingMcpEquipmentMappingBatchItemSchema).min(1).max(100),
});
export const advertisingMcpStageProposalSchema = z.object({
  platformCode: advertisingMcpPlatformCodeSchema,
  proposalId: z.string().uuid(),
  idempotencyKey: z.string().uuid(),
});
export const advertisingMcpStageProposalBatchSchema = z.object({
  platformCode: advertisingMcpPlatformCodeSchema,
  proposals: z
    .array(
      z.object({
        proposalId: z.string().uuid(),
        idempotencyKey: z.string().uuid(),
      })
    )
    .min(1)
    .max(100),
});
export const advertisingMcpValidateMappingBatchSchema = z.object({
  platformCode: advertisingMcpPlatformCodeSchema,
  domain: z.enum(['vehicle', 'equipment']),
  runId: z.string().uuid().optional(),
});
